import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {withTranslation} from 'react-i18next';
import {useHistory} from 'react-router-dom';
import axios from 'axios';
import {Button} from 'primereact/button';
import {Dropdown} from 'primereact/dropdown';
import {Tag} from 'primereact/tag';

const SalesDocumentChainComponent = ({dealId, t}) => {
  const history = useHistory();
  const [chain, setChain] = useState(null);
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [loading, setLoading] = useState(false);
  const headers = useMemo(() => ({Authorization: 'Bearer ' + localStorage.getItem('userToken'), 'App-Network': localStorage.getItem('networkId')}), []);
  const canWriteProject = (localStorage.getItem('roles') || '').toLowerCase().includes('role_project_write');

  const load = useCallback(() => {
    if (!dealId) return;
    setLoading(true);
    Promise.all([
      axios.get(process.env.REACT_APP_API_HOST + '/api/v2/crm-deal/' + dealId + '/sales-chain', {headers}),
      axios.get(process.env.REACT_APP_API_HOST + '/api/v2/receipt-group?item_per_page=200', {headers})
    ]).then(([chainResponse, groupResponse]) => {
      setChain(chainResponse.data.data);
      const available = (groupResponse.data.data.items || []).filter(group => group.active && group.receipt_type === 'CUSTOMER_INVOICE');
      setGroups(available);
      setSelectedGroup(current => current || available[0]?.id || null);
    }).catch(error => window.App.toastShow('error', t('error'), error.toString(), '', error.response))
      .finally(() => setLoading(false));
  }, [dealId, headers, t]);
  useEffect(() => load(), [load]);

  const createOrder = offer => {
    setLoading(true);
    axios.post(process.env.REACT_APP_API_HOST + '/api/v2/offer/' + offer.id + '/create-project', {copyAllItems: true}, {headers})
      .then(() => { window.App.toastShow('success', t('crmSalesChainOrderCreated')); load(); })
      .catch(error => window.App.toastShow('error', t('error'), error.toString(), '', error.response))
      .finally(() => setLoading(false));
  };
  const createFulfillment = order => {
    setLoading(true);
    axios.post(process.env.REACT_APP_API_HOST + '/api/v2/crm-deal/' + dealId + '/sales-chain/fulfillment', {projectId: order.id}, {headers})
      .then(() => { window.App.toastShow('success', t('crmSalesChainFulfillmentCreated')); load(); })
      .catch(error => window.App.toastShow('error', t('error'), error.toString(), '', error.response))
      .finally(() => setLoading(false));
  };
  const createReceipt = fulfillment => {
    if (!selectedGroup) return;
    setLoading(true);
    axios.post(process.env.REACT_APP_API_HOST + '/api/v2/fulfillment/' + fulfillment.id + '/create-receipt/' + selectedGroup, {}, {headers})
      .then(response => {
        window.App.toastShow('success', t('crmSalesChainInvoiceCreated'));
        load();
        history.push('/finance/receipt/read/' + response.data.data.id);
      })
      .catch(error => window.App.toastShow('error', t('error'), error.toString(), '', error.response))
      .finally(() => setLoading(false));
  };
  const money = item => item.net_amount == null ? '—' : window.App.currencyFormatter(item.currency || 'HUF', item.net_amount);
  const card = (item, kind) => <article key={kind + '-' + item.id} className="crm-sales-chain-card">
    <strong>{item.number || item.name || ('#' + item.id)}</strong>
    {item.number && item.name && <span>{item.name}</span>}
    {item.status && <Tag value={item.status}/>}<small>{money(item)}</small>
    <Button type="button" icon="pi pi-eye" className="p-button-text p-button-sm" label={t('view')} onClick={() => {
      if (kind === 'offer') history.push('/project/offer/read/' + item.id);
      if (kind === 'order') history.push('/project/project/read/' + item.id);
      if (kind === 'fulfillment') history.push('/project/project/read/' + item.project_id + '?update=fulfillment&fulfillmentId=' + item.id);
      if (kind === 'receipt') history.push('/finance/receipt/read/' + item.id);
    }}/>
  </article>;

  if (!chain) return <div className={loading ? 'crm-busy' : ''}/>;
  const unconvertedOffer = chain.offers.find(offer => !offer.project_id);
  const orderWithoutFulfillment = chain.orders.find(order => !chain.fulfillments.some(item => Number(item.project_id) === Number(order.id)));
  const fulfillmentWithoutReceipt = chain.fulfillments.find(item => !item.receipt_id);
  return <section className={'crm-sales-chain ' + (loading ? 'crm-busy' : '')}>
    <div className="crm-sales-chain-heading"><div><h3>{t('crmSalesDocumentChain')}</h3><p>{t('crmSalesDocumentChainHint')}</p></div><Button type="button" icon="pi pi-refresh" className="p-button-text" onClick={load}/></div>
    <div className="crm-sales-chain-grid">
      <div className="crm-sales-chain-stage"><h4>1. {t('crmOpportunities')}</h4><article className="crm-sales-chain-card"><strong>{chain.deal.name}</strong><Tag severity="info" value={'#' + chain.deal.id}/></article></div>
      <div className="crm-sales-chain-arrow">→</div>
      <div className="crm-sales-chain-stage"><h4>2. {t('offerList')}</h4>{chain.offers.map(item => card(item, 'offer'))}{canWriteProject && <Button type="button" icon="pi pi-plus" label={t('crmCreateOffer')} className="p-button-outlined p-button-sm" onClick={() => history.push('/project/offer/create?crmDealId=' + dealId + '&name=' + encodeURIComponent(chain.deal.name || '') + '&partnerId=' + (chain.deal.partner_id || '') + '&partnerName=' + encodeURIComponent(chain.deal.partner_name || ''))}/>}</div>
      <div className="crm-sales-chain-arrow">→</div>
      <div className="crm-sales-chain-stage"><h4>3. {t('crmSalesOrder')}</h4>{chain.orders.map(item => card(item, 'order'))}{unconvertedOffer && canWriteProject && <Button type="button" icon="pi pi-arrow-right" label={t('crmCreateOrderFromOffer')} className="p-button-outlined p-button-sm" onClick={() => createOrder(unconvertedOffer)}/>}</div>
      <div className="crm-sales-chain-arrow">→</div>
      <div className="crm-sales-chain-stage"><h4>4. {t('crmFulfillmentDelivery')}</h4>{chain.fulfillments.map(item => card(item, 'fulfillment'))}{orderWithoutFulfillment && canWriteProject && <Button type="button" icon="pi pi-arrow-right" label={t('crmCreateFulfillment')} className="p-button-outlined p-button-sm" onClick={() => createFulfillment(orderWithoutFulfillment)}/>}</div>
      <div className="crm-sales-chain-arrow">→</div>
      <div className="crm-sales-chain-stage"><h4>5. {t('receiptInvoice')}</h4>{chain.receipts.map(item => card(item, 'receipt'))}{fulfillmentWithoutReceipt && canWriteProject && <div className="crm-sales-chain-next"><Dropdown value={selectedGroup} options={groups} optionLabel="name" optionValue="id" placeholder={t('receiptGroup')} onChange={event => setSelectedGroup(event.value)}/><Button type="button" icon="pi pi-arrow-right" label={t('crmCreateInvoice')} disabled={!selectedGroup} className="p-button-outlined p-button-sm" onClick={() => createReceipt(fulfillmentWithoutReceipt)}/></div>}</div>
    </div>
    {chain.contracts.length > 0 && <div className="crm-sales-chain-contracts"><b>{t('contractList')}:</b> {chain.contracts.map(item => item.number || item.name).join(', ')}</div>}
  </section>;
};

export default withTranslation('common')(SalesDocumentChainComponent);
